'use client';

import { useEffect, useMemo, useState } from 'react';

import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { useDisclosure } from '@mantine/hooks';

import { IconLayoutGrid, IconList } from '@tabler/icons-react';

import {
  EmptyStateView,
  Group,
  ListRowCardView,
  SearchInput,
  SegmentedInputView,
  SimpleGrid,
  Stack,
  StandardLayoutView,
} from '@optiro/ui';

import { ResourceDrawer } from '@/components/ResourceDrawer';
import { useAccounts } from '@/hooks/useAccounts';
import { useInvitations } from '@/hooks/useInvitations';
import InvitationRow from '@/modules/Resources/InvitationRow/InvitationRow';
import { ResourceInviteForm } from '@/modules/Resources/ResourceInviteForm';
import { ResourcesDataTable } from '@/modules/Resources/ResourcesDataTable';
import ResourceRow from '@/modules/Resources/ResourceRow/ResourceRow';

export const Route = createFileRoute('/app/resources/')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    openResourceId: typeof search.openResourceId === 'string' ? search.openResourceId : undefined,
    tab: typeof search.tab === 'string' ? search.tab : undefined,
  }),
});

const SKELETON_CARD_COUNT = 6;

function RouteComponent() {
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'grid' | 'table'>('grid');
  const { data: accounts, isLoading, isError } = useAccounts();
  const { data: invitations } = useInvitations();
  const navigate = useNavigate();
  const { openResourceId, tab } = Route.useSearch();
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  useEffect(() => {
    if (openResourceId) openDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openResourceId]);

  const handleCloseDrawer = () => {
    closeDrawer();
    navigate({ to: '/app/resources', search: { openResourceId: undefined, tab: undefined } });
  };

  const toolbar = (
    <Group justify="space-between" align="center" wrap="wrap">
      <SearchInput
        collapsible
        placeholder="Søg medarbejdere…"
        value={search}
        onChange={setSearch}
        size="xs"
      />
    </Group>
  );

  // Filter resources based on search term
  const filteredResources = useMemo(() => {
    if (!accounts) return [];

    const searchLower = search.toLowerCase();
    return accounts.filter(
      (resource) =>
        resource.name?.toLowerCase().includes(searchLower) ||
        resource.email?.toLowerCase().includes(searchLower)
    );
  }, [accounts, search]);

  const filteredInvitations = useMemo(() => {
    if (!invitations) return [];

    const searchLower = search.toLowerCase();
    return invitations.filter(
      (invitation) =>
        invitation.name?.toLowerCase().includes(searchLower) ||
        invitation.email.toLowerCase().includes(searchLower)
    );
  }, [invitations, search]);

  const content = (() => {
    if (isError) {
      return (
        <EmptyStateView
          variant="feature"
          title="An error occurred while loading data"
          description="Please try refreshing the page or contact support if the problem persists."
        />
      );
    }

    if (!isLoading && filteredResources.length === 0 && filteredInvitations.length === 0) {
      return (
        <EmptyStateView
          variant="feature"
          title="No data found"
          description="There are no items to display at the moment."
        />
      );
    }

    return (
      <Stack gap="sm">
        <Group justify="flex-end">
          <SegmentedInputView
            value={view}
            onChange={(v) => setView(v as 'grid' | 'table')}
            data={[
              { value: 'grid', label: <IconLayoutGrid size={16} /> },
              { value: 'table', label: <IconList size={16} /> },
            ]}
          />
        </Group>
        {view === 'table' ? (
          <ResourcesDataTable resources={filteredResources} fetching={isLoading} />
        ) : (
          <SimpleGrid cols={{ base: 1, md: 2 }}>
            {isLoading && filteredResources.length === 0
              ? Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
                  <ListRowCardView key={i} loading name="" />
                ))
              : [
                  ...filteredResources.map((resource) => (
                    <ResourceRow key={`account-${resource.id}`} resource={resource} />
                  )),
                  ...filteredInvitations.map((invitation) => (
                    <InvitationRow key={`invitation-${invitation.id}`} invitation={invitation} />
                  )),
                ]}
          </SimpleGrid>
        )}
      </Stack>
    );
  })();

  return (
    <StandardLayoutView title="Medarbejdere" actions={<ResourceInviteForm />} toolbar={toolbar}>
      {openResourceId && (
        <ResourceDrawer
          opened={drawerOpened}
          onClose={handleCloseDrawer}
          resourceId={openResourceId}
          initialTab={tab}
        />
      )}
      {content}
    </StandardLayoutView>
  );
}
